import React from "react";
import { Card, Divider, Empty, Timeline, Typography } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  UserAddOutlined,
  RocketOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const { Text, Title } = Typography;

interface Props {
  taskDetail: any[];
}

interface EventDate {
  result_date?: string;
  start_date?: string;
  end_date?: string;
  award_date?: string;
  start_event_date?: string;
  start_regis_date?: string;
  end_regis_date?: string;
}

const TabTimeline: React.FC<Props> = ({ taskDetail }) => {
  const { t } = useTranslation();

  const formatThaiDateTime = (dateString: string) => {
    if (!dateString) return "-";
    return dayjs(dateString).locale("th").format("D MMMM BBBB เวลา HH:mm:ss");
  };

  const parseEventDate = (
    eventDateData: string | EventDate | any
  ): EventDate => {
    try {
      if (!eventDateData) return {};
      if (typeof eventDateData === "string") {
        const parsed = JSON.parse(eventDateData);
        return Array.isArray(parsed) ? parsed[0] || {} : parsed;
      }
      if (Array.isArray(eventDateData)) {
        return eventDateData[0] || {};
      }
      if (typeof eventDateData === "object") {
        return eventDateData;
      }
      return {};
    } catch (error) {
      console.error("Error parsing event_date:", error);
      return {};
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <p style={{ margin: 10, fontSize: 16, fontWeight: "bold" }}>
        {t("Timeline")}
      </p>
      <Divider />

        {taskDetail.length > 0 ? (
          taskDetail.map((task) => {
            const eventDate = parseEventDate(task.event_date);

            // ตรวจสอบว่ามีข้อมูลวันที่หรือไม่
            const hasAnyDate =
              eventDate.start_regis_date ||
              eventDate.end_regis_date ||
              eventDate.start_event_date ||
              eventDate.start_date ||
              eventDate.end_date ||
              eventDate.result_date ||
              eventDate.award_date;

            // ถ้าไม่มีข้อมูลวันที่เลย
            if (!hasAnyDate) {
              return (
                <Empty description={t("No timeline available")} />
              );
            }

            // สร้าง timeline items
            const timelineItems = [];

            if (eventDate.start_event_date) {
              timelineItems.push({
                color: "blue",
                dot: <RocketOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      วันประกาศกิจกรรม
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.start_event_date)}
                    </Text>
                  </div>
                ),
              });
            }

            if (eventDate.start_regis_date) {
              timelineItems.push({
                color: "green",
                dot: <UserAddOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      วันเริ่มลงทะเบียน
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.start_regis_date)}
                    </Text>
                  </div>
                ),
              });
            }

            if (eventDate.end_regis_date) {
              timelineItems.push({
                color: "orange",
                dot: <UserAddOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      วันสิ้นสุดลงทะเบียน
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.end_regis_date)}
                    </Text>
                  </div>
                ),
              });
            }

            

            if (eventDate.start_date) {
              timelineItems.push({
                color: "blue",
                dot: <CalendarOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      วันเริ่มกิจกรรม
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.start_date)}
                    </Text>
                  </div>
                ),
              });
            }

            if (eventDate.end_date) {
              timelineItems.push({
                color: "red",
                dot: <CalendarOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      วันสิ้นสุดกิจกรรม
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.end_date)}
                    </Text>
                  </div>
                ),
              });
            }

            if (eventDate.result_date) {
              timelineItems.push({
                color: "purple",
                dot: <CheckCircleOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      วันประกาศผล
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.result_date)}
                    </Text>
                  </div>
                ),
              });
            }

            if (eventDate.award_date) {
              timelineItems.push({
                color: "gold",
                dot: <TrophyOutlined style={{ fontSize: "16px" }} />,
                children: (
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      {t("Award Date")}
                    </Text>
                    <br />
                    <Text type="secondary">
                      <ClockCircleOutlined />{" "}
                      {formatThaiDateTime(eventDate.award_date)}
                    </Text>
                  </div>
                ),
              });
            }

            return (
              <Timeline
                key={task.id}
                mode="left"
                items={timelineItems}
                style={{ marginTop: 20 }}
              />
            );
          })
        ) : (
        <Empty description={t("No timeline available")} />
        )}
      </Card>
    </>
  );
};

export default TabTimeline;
